import { toast } from '../toast.js';

/** Must match the fade-out on `.fetch-overlay[data-closing]` in compat.css. */
const OVERLAY_EXIT_MS = 140;

export class OverlayManager {
  constructor() {
    this.abortController = null;
    this.watchdog = null;
    this.closeTimer = null;
    this.overlay = document.getElementById('fetchOverlay');
    this.messageEl = document.getElementById('fetchOverlayMessage');
    this.subtextEl = document.getElementById('fetchOverlaySubtext');
    this.cancelBtn = document.getElementById('fetchOverlayCancelBtn');
    this.spinner = this.overlay?.querySelector('.fetch-overlay-spinner');
  }

  /**
   * @param {object} opts
   * @param {number} [opts.timeout] Milliseconds before the overlay gives up on
   *   itself. A blocking spinner with no end is worse than an error: the
   *   operator cannot tell a slow network from a dropped request, and the page
   *   is unusable either way. Every show gets one.
   */
  show(message, { subtext = '', cancellable = false, spinnerColor = '', timeout = 60000 } = {}) {
    if (!this.overlay || !this.messageEl || !this.subtextEl || !this.cancelBtn) {
      console.warn('Overlay elements not found');
      return;
    }

    this.messageEl.textContent = message;

    if (subtext) {
      this.subtextEl.textContent = subtext;
      this.subtextEl.classList.remove('hidden');
    } else {
      this.subtextEl.classList.add('hidden');
    }

    if (spinnerColor && this.spinner) {
      this.spinner.style.borderTopColor = spinnerColor;
    } else if (this.spinner) {
      this.spinner.style.borderTopColor = '';
    }

    if (cancellable) {
      this.abortController = new AbortController();
      this.cancelBtn.classList.remove('hidden');
      this.cancelBtn.onclick = () => {
        if (this.abortController) this.abortController.abort();
        this.hide();
        toast.info('Request cancelled.');
      };
    } else {
      this.cancelBtn.classList.add('hidden');
      this.cancelBtn.onclick = null;
    }

    // Cancel a fade-out still in flight, or the freshly shown overlay would
    // finish someone else's exit animation and vanish.
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    delete this.overlay.dataset.closing;

    this.overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';

    clearTimeout(this.watchdog);
    this.watchdog = setTimeout(() => {
      if (this.abortController) this.abortController.abort();
      this.hide();
      toast.error('That is taking longer than expected. Nothing was changed — please try again.');
    }, timeout);
  }

  /**
   * Fade out rather than cut. The overlay fades in, so cutting it away made
   * every short request look like a flicker; the page underneath is already
   * interactive during the fade, since pointer events stop immediately.
   */
  hide() {
    if (!this.overlay) return;

    clearTimeout(this.watchdog);
    this.watchdog = null;
    document.body.style.overflow = '';
    this.abortController = null;

    if (this.overlay.classList.contains('hidden')) return;

    this.overlay.dataset.closing = 'true';
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.overlay.classList.add('hidden');
      delete this.overlay.dataset.closing;
      this.closeTimer = null;
    }, OVERLAY_EXIT_MS);
  }

  updateMessage(message, subtext) {
    if (this.messageEl) {
      this.messageEl.textContent = message;
    }
    
    if (subtext !== undefined && this.subtextEl) {
      this.subtextEl.textContent = subtext;
      this.subtextEl.classList.toggle('hidden', !subtext);
    }
  }

  /** True while the blocking spinner is on screen. */
  isVisible() {
    return !!this.overlay && !this.overlay.classList.contains('hidden');
  }

  getAbortSignal() {
    return this.abortController?.signal;
  }
}
